import React from 'react';
import { Link } from 'react-router-dom';

const PLAYTEST_URL = 'https://farshore.netlify.app/?playtest=1';
const FRESH_URL = 'https://farshore.netlify.app/?playtest=new';
const OFF_URL = 'https://farshore.netlify.app/?playtest=0';
const FEEDBACK_EMAIL =
  'mailto:longden@longden.net?subject=Farshore%20playtest';

const post = {
  slug: 'farshore-playtest',
  title: 'Play Farshore with us: an open playtest through October',
  date: '2026-09-23',
  cover: '/blog/farshore/shore.jpg',
  coverAlt:
    'An open shore with a skiff at a pier and a squat stone tower across the water',
  coverCaption: 'The far shore. The beacon is waiting.',
  excerpt:
    'Throughout October we are running an open playtest of Farshore. No sign-up, nothing to install: open the playtest link in a desktop browser, play, and the game tells us where it creaks.',
  content: (
    <>
      <p>
        Farshore has grown. What started as a short walk through a sealed
        Conservatory is now eight chapters and a coda, about 45 to 60 minutes
        from the brick tunnel to the end. Before we take it any further, we
        want to hear from people who are not us. So throughout October we are
        running an open playtest, and you are invited. There is nothing to
        sign up for and nothing to install. The playtest link is live now, so
        if you want a head start before the first of the month, go ahead.
      </p>

      <figure className="post-figure">
        <div className="post-video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/wipuulcLxms"
            title="Farshore trailer"
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
        <figcaption>The trailer, rendered and recorded from the game itself.</figcaption>
      </figure>

      <h2>What this playtest is for</h2>
      <p>
        This is a polish pass, not a content drop. Every chapter is there,
        start to finish. What we want is to make the game that already exists
        as good as it can be.
      </p>
      <p>
        That means the unglamorous work. The moment where you stood in a room
        for three minutes not knowing what to do. The stair that caught your
        foot. The journal line that promised something the world did not
        deliver. The frame rate on the laptop you actually own, not the one we
        built it on. We made every system in Farshore by hand, which means
        every rough edge is ours to find and ours to fix. We would rather you
        found them for us.
      </p>

      <h2>How to take part</h2>
      <p>
        You need a desktop or laptop browser, a mouse and keyboard, and about
        an hour. Sound on, and headphones if you have them. Phones get a
        polite &ldquo;play this on a computer&rdquo; card instead of the game.
      </p>
      <ul>
        <li>
          Open the playtest link:{' '}
          <a href={PLAYTEST_URL}>farshore.netlify.app/?playtest=1</a>. The
          ordinary link without <code>?playtest=1</code> works as a game but
          sends us nothing, so please use this one.
        </li>
        <li>
          Read the short <strong>Playtest build</strong> notice on the title
          card, then click Begin.
        </li>
        <li>
          WASD to move, mouse to look, E to interact, Space to jump, J to
          reread the journal. Esc pauses. If you ever get physically wedged
          somewhere, R unsticks you. If it feels choppy, press Esc, open
          Settings and set Render quality to Performance or Low (G is a
          shortcut). The game will also step it down for you if the first
          few seconds run slowly. There is music now too, quiet on purpose:
          M turns it off and on, and Settings has its own level.
        </li>
        <li>
          Play it the way you would play any game. Please do not go looking
          for bugs on purpose, and please do not look anything up. Getting
          stuck is useful, not a failure. It is exactly what we want to see.
        </li>
        <li>
          You do not have to finish in one sitting. The session sticks to
          your browser, so reloading, switching chapters or coming back
          tomorrow all carry on the same playtest.
        </li>
      </ul>

      <div className="post-photo-grid">
        <img
          src="/blog/farshore/lens.jpg"
          alt="A brass lens held up to an arrow slit, focusing a thin thread of light"
        />
        <img
          src="/blog/farshore/beacon.jpg"
          alt="Looking down the beacon tower's spiral to a flooded floor where a small tree stands in the water"
        />
      </div>

      <h2>What the game records</h2>
      <p>
        The playtest link switches on a small recorder built into the game,
        and the notice on the title card says exactly what it keeps. It notes
        which story moments you reach and when, what you examine and pick up,
        which hints appear, where you walk, and your frame rate along with
        the name of your graphics chip. Time only counts while you are
        actually playing, so stepping away to make tea does not look like
        being stuck. Nothing else about you is collected: no name, no email,
        no account. Like any website, our host (Netlify) also sees your IP
        address and browser type when the log arrives, and the log includes
        your screen size.
      </p>
      <p>
        That log is sent to us when you finish, when you press the feedback
        button, or when you close the tab. If you play for less than half a
        minute and send no feedback, nothing is sent at all.
      </p>

      <h2>Telling us what you thought</h2>
      <p>
        The recorder tells us where people get lost. It cannot tell us how
        the game felt, so there are three small ways to add that, all inside
        the game:
      </p>
      <ul>
        <li>
          <strong>Rate each chapter.</strong> Every chapter card has a quick
          one-to-five rating and room for a short note. One tap is plenty.
        </li>
        <li>
          <strong>Stop whenever you like.</strong> Press Esc and choose{' '}
          <em>I&rsquo;m done &mdash; send feedback</em>. Giving up halfway is just
          as useful to us as finishing, so please do not feel you owe us the
          ending.
        </li>
        <li>
          <strong>Answer a few questions at the end.</strong> After the final
          card, a short form opens by itself: where you got stuck, a quick
          question about the story, whether you would have kept playing, the
          moment you remember most, and anything else on your mind.
        </li>
      </ul>

      <div className="post-callout">
        <h3>If something goes wrong</h3>
        <p>
          If the game says it could not send your feedback, press Esc and
          choose <em>Download my log</em>. That saves a small file you can
          email to <a href={FEEDBACK_EMAIL}>longden@longden.net</a> with
          &ldquo;Farshore playtest&rdquo; in the subject. The same address is
          the place for anything that does not fit the form: a screenshot of a
          bug, a thought you had the next morning, or a single sentence like
          &ldquo;I got stuck in the tower&rdquo;.
        </p>
        <p>
          Sharing a computer with someone else who wants to play? Have them
          open <a href={FRESH_URL}>?playtest=new</a> so their session starts
          fresh instead of continuing yours. To switch the recorder off
          entirely, open <a href={OFF_URL}>?playtest=0</a>.
        </p>
      </div>

      <h2>What happens next</h2>
      <p>
        We will read everything that comes in and fix as we go, so the build
        you play at the end of October may be a little different from the
        one you play at the start. If you played early and something has
        changed, that is probably because somebody told us about it. When the
        playtest wraps up we will write about what we learned here on the
        blog, the good and the embarrassing.
      </p>
      <p>
        If you want the background first,{' '}
        <Link to="/blog/farshore">our first post about Farshore</Link> covers
        how it was built, with no engine and no audio files. Otherwise,{' '}
        <a href={PLAYTEST_URL}>open the playtest</a> and go. Water the
        seedling. Mind the light.
      </p>
      <p>
        Farshore also plays right on its{' '}
        <a href="https://christopherlongden.itch.io/farshore">itch.io page</a>,
        where you can follow it as it heads for release.
      </p>
      <p>
        <em>
          Writing about Farshore? The{' '}
          <a href="https://farshore.netlify.app/press/">press kit</a> has the
          facts, screenshots and the trailer.
        </em>
      </p>
      <p className="post-signoff">Thanks for helping us make it better.</p>
    </>
  ),
};

export default post;
